"use client";

/**
 * use-toast — the legacy shadcn toast API routed onto THE one rounded card.
 *
 * Operator ruling (live-test 2026-08-18): round desk cards ONLY — the radix
 * rectangle renderer is retired. Every existing `toast({ title, variant })`
 * call site keeps its API; the render is the DeskToast onyx card on sonner.
 * Variants map to tones; risk variants drop the Donna avatar (SIG-1214: no
 * mascot fronts a halt). The old in-memory toast store and its radix
 * <Toaster/> are gone — sonner owns lifecycle (stacking, replace-by-id,
 * dismissal).
 */

import type * as React from "react";
import { toast as sonnerToast } from "sonner";

import {
  showDeskToast,
  type DeskToastTone,
} from "@/components/notifications/desk-toast";

export type ToastVariant =
  | "default"
  | "info"
  | "success"
  | "warning"
  | "error"
  | "destructive";

export interface ToastOptions {
  /** Stable id → singleton semantics: a re-emit with the same id REPLACES the
   * visible card in place instead of stacking (sonner replace-by-id). Use for
   * recurring emitters (update checker polls, reconnect loops). */
  id?: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  variant?: ToastVariant | null;
  duration?: number;
  /** One plain action button (label + handler). Radix ToastAction elements
   * are retired with the rectangle renderer. */
  action?: { label: string; onClick: () => void };
}

export function toastPresentationFor(variant: ToastVariant | null | undefined): {
  tone: DeskToastTone;
  eyebrow: string;
  avatar: boolean;
} {
  switch (variant) {
    case "destructive":
    case "error":
      return { tone: "rose", eyebrow: "Alert", avatar: false };
    case "warning":
      return { tone: "rose", eyebrow: "Heads up", avatar: false };
    case "success":
      return { tone: "mint", eyebrow: "Done", avatar: true };
    default:
      return { tone: "neutral", eyebrow: "Desk update", avatar: true };
  }
}

let count = 0;
function genId(): string {
  count = (count + 1) % Number.MAX_VALUE;
  return `legacy-toast-${count}`;
}

function present(id: string, options: ToastOptions): void {
  const presentation = toastPresentationFor(options.variant);
  showDeskToast({
    id,
    eyebrow: presentation.eyebrow,
    tone: presentation.tone,
    avatar: presentation.avatar,
    title: options.title ?? "",
    description: options.description,
    action: options.action,
    // Operator video ruling 2026-08-18 (~2:20-2:45): action-relevant toasts
    // were auto-closing while being read. A toast carrying an action button
    // persists until acted on/dismissed unless the caller set a duration.
    duration: options.duration ?? (options.action ? Infinity : undefined),
  });
}

// Operator lock (Tella clip 2026-07-22 0:39, carried over from the radix
// store's TOAST_LIMIT=1): ONE visible legacy toast — a new one CANCELS the
// previous instead of stacking. update() reuses its own id and never cancels.
// Refined 2026-08-18 (operator video): only PASSIVE toasts are cancellable —
// an action-relevant toast is never auto-cancelled by later arrivals (rapid
// emit waves were closing toasts while the operator read them).
let lastCancellableToastId: string | null = null;

function toast(options: ToastOptions) {
  const id = options.id ?? genId();
  if (lastCancellableToastId !== null && lastCancellableToastId !== id) {
    sonnerToast.dismiss(lastCancellableToastId);
    lastCancellableToastId = null;
  }
  if (!options.action) {
    lastCancellableToastId = id;
  }
  present(id, options);
  return {
    id,
    dismiss: () => sonnerToast.dismiss(id),
    // Same stable sonner id → the card replaces in place.
    update: (next: ToastOptions) => present(id, { ...options, ...next }),
  };
}

function dismiss(toastId?: string) {
  sonnerToast.dismiss(toastId);
}

/** Shape the retired radix renderer (ui/toaster.tsx) destructures — kept so
 * it still compiles; the array is permanently empty. */
type LegacyToastRecord = {
  id: string;
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  [key: string]: unknown;
};

/** API-compatible hook for the 40+ existing call sites. The `toasts` array is
 * always empty — sonner owns state; nothing subscribes to a store anymore. */
function useToast() {
  return { toast, dismiss, toasts: [] as LegacyToastRecord[] };
}

export { useToast, toast };
